import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AppIcon } from './ui/icon'
import { mdiArchiveArrowDownOutline } from '@mdi/js'
import { Button } from './ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog'
import { Label } from './ui/label'
import { Textarea } from './ui/textarea'
import { useArchiveAsset } from '../hooks/useQueries'
import { ComponentsDetachedNotice } from './equipment/ComponentsCard'

interface AssetArchiveDialogProps {
  asset: {
    uid: string
    name?: string | null
    children_count?: number | null
  }
  org: string
  onArchived: () => void
  /** Controlled mode: the caller (the header's « … » menu) owns the trigger. */
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export function AssetArchiveDialog({ asset, org, onArchived, open: openProp, onOpenChange }: AssetArchiveDialogProps) {
  const { t } = useTranslation('assets')
  const archiveMutation = useArchiveAsset(org)

  const [openState, setOpenState] = useState(false)
  const controlled = openProp !== undefined
  const open = controlled ? openProp : openState
  const setOpen = (o: boolean) => {
    if (!controlled) setOpenState(o)
    onOpenChange?.(o)
  }
  const [reason, setReason] = useState('')
  const [isArchiving, setIsArchiving] = useState(false)

  const hasChildren = (asset.children_count ?? 0) > 0

  const handleConfirm = async () => {
    setIsArchiving(true)
    try {
      await archiveMutation.mutateAsync({
        assetUid: asset.uid,
        reason: reason.trim() || undefined,
      })
      setOpen(false)
      setReason('')
      onArchived()
    } catch {
      // Error is handled by the mutation state
    } finally {
      setIsArchiving(false)
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setReason('') } }}>
      {!controlled && (
        <AlertDialogTrigger asChild>
          <Button
            variant="destructive"
            size="sm"
            data-testid="archive-asset-btn"
            aria-label={t('archive.button')}
            title={t('archive.button')}
          >
            <AppIcon path={mdiArchiveArrowDownOutline} size="sm" className="md:mr-1" />
            <span className="hidden md:inline">{t('archive.button')}</span>
          </Button>
        </AlertDialogTrigger>
      )}
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('archive.title')}</AlertDialogTitle>
          <AlertDialogDescription>
            {hasChildren
              ? t('archive.confirm_with_children', { name: asset.name, count: asset.children_count })
              : t('archive.confirm_simple', { name: asset.name })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <ComponentsDetachedNotice org={org} assetUid={asset.uid} enabled={open} />

        <div className="space-y-2 py-2">
          <Label htmlFor="archive-reason" className="text-sm">{t('archive.reason_label')}</Label>
          <Textarea
            id="archive-reason"
            placeholder={t('archive.reason_placeholder')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            data-testid="archive-reason-input"
          />
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel data-testid="cancel-archive-asset-btn">
            {t('common:actions.cancel', 'Cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isArchiving}
            data-testid="confirm-archive-asset-btn"
          >
            <AppIcon path={mdiArchiveArrowDownOutline} size="sm" className="mr-1" />
            {isArchiving ? t('archive.archiving') : t('archive.button')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
